import { useRef } from "react";
import type { KeyboardEvent } from "react";

export type TabItem<T extends string> = {
  value: T;
  label: string;
};

type TabsProps<T extends string> = {
  tabs: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  idPrefix: string;
  ariaLabel: string;
};

export default function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  idPrefix,
  ariaLabel,
}: TabsProps<T>) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.value === value);
    const nextIndex =
      event.key === "ArrowRight"
        ? (index + 1) % tabs.length
        : (index - 1 + tabs.length) % tabs.length;
    const next = tabs[nextIndex];
    onChange(next.value);
    window.requestAnimationFrame(() => refs.current[next.value]?.focus());
  }

  return (
    <div role="tablist" aria-label={ariaLabel} className="mb-6 flex border-b border-gray-200">
      {tabs.map((tab) => {
        const selected = value === tab.value;
        return (
          <button
            key={tab.value}
            ref={(element) => {
              refs.current[tab.value] = element;
            }}
            id={`${idPrefix}-tab-${tab.value}`}
            role="tab"
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.value)}
            onKeyDown={handleKeyDown}
            className={`border-b-2 px-4 py-2.5 text-sm font-medium focus:outline-none focus-visible:rounded-t-md focus-visible:bg-blue-50 focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-4 ${selected ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
